import React, { useState, useEffect } from "react";
import config from '../../config/config';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Login.css";
import { useAuth } from "../login/AuthContext";
import loginReklam from "../../public/ofisinizheryerde.png";
import { getDeviceId, getComputerName } from "../../utils/deviceIdentifier";

const REMEMBER_KEYS = { username: "login_remember_username", bayiKodu: "login_remember_bayiKodu", password: "login_remember_password", checked: "login_remember_checked" };

const isNativeMobile = () => typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [bayiKodu, setBayiKodu] = useState("");
  const [beniHatirla, setBeniHatirla] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [attemptInfo, setAttemptInfo] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Mobil (Capacitor) veya dar ekran: mobil düzen kullan
  useEffect(() => {
    setIsMobile(isNativeMobile() || (typeof window !== "undefined" && window.innerWidth <= 768));
    const onResize = () => setIsMobile(isNativeMobile() || window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Sayfa açıldığında "Beni hatırla" ile kaydedilmiş bilgileri doldur
  useEffect(() => {
    try {
      const checked = localStorage.getItem(REMEMBER_KEYS.checked) === "1";
      setBeniHatirla(checked);
      if (checked) {
        const u = localStorage.getItem(REMEMBER_KEYS.username);
        const b = localStorage.getItem(REMEMBER_KEYS.bayiKodu);
        const p = localStorage.getItem(REMEMBER_KEYS.password);
        if (u) setUsername(u);
        if (b) setBayiKodu(b);
        if (p) setPassword(p);
      }
    } catch (_) {}
  }, []);

  // Engelleme seviyeleri
  const BLOCK_LEVELS = [
    { threshold: 3, duration: 30, label: "30 saniye" },
    { threshold: 5, duration: 60, label: "1 dakika" },
    { threshold: 7, duration: 300, label: "5 dakika" },
    { threshold: 10, duration: 900, label: "15 dakika" },
    { threshold: 15, duration: 3600, label: "1 saat" },
    { threshold: 20, duration: 10800, label: "3 saat" },
    { threshold: 25, duration: 86400, label: "24 saat" },
  ];

  // Geri sayım
  useEffect(() => {
    let interval;
    if (isBlocked && blockTimer > 0) {
      interval = setInterval(() => {
        setBlockTimer((prev) => {
          if (prev <= 1) {
            setIsBlocked(false);
            setErrorMessage("");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isBlocked, blockTimer]);

  // Süreyi formatla
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Sonraki engel süresini bul
  const getNextBlockDuration = (attempts) => {
    for (const level of BLOCK_LEVELS) {
      if (attempts < level.threshold) {
        return level;
      }
    }
    return BLOCK_LEVELS[BLOCK_LEVELS.length - 1];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (isBlocked) return;

    if (!username.trim() || !password.trim() || !bayiKodu.trim()) {
      setErrorMessage("Tüm alanların doldurulması zorunludur!");
      return;
    }

    setIsLoading(true);

    try {
      // Cihaz tanımlayıcısını al
      const deviceId = getDeviceId();
      const computerName = getComputerName();
      
      const response = await axios.post(
        `${config.baseURL}/login`,
        {
          username,
          password,
          bayiKodu,
          deviceId,
          computerName,
        },
        {
          headers: {
            'X-Device-Id': deviceId,
            'X-Computer-Name': computerName,
          },
          timeout: 20000,
        }
      );

      if (response.data.success && response.data.token) {
        try {
          if (beniHatirla) {
            localStorage.setItem(REMEMBER_KEYS.checked, "1");
            localStorage.setItem(REMEMBER_KEYS.username, username.trim());
            localStorage.setItem(REMEMBER_KEYS.bayiKodu, bayiKodu.trim());
            localStorage.setItem(REMEMBER_KEYS.password, password);
          } else {
            localStorage.removeItem(REMEMBER_KEYS.checked);
            localStorage.removeItem(REMEMBER_KEYS.username);
            localStorage.removeItem(REMEMBER_KEYS.bayiKodu);
            localStorage.removeItem(REMEMBER_KEYS.password);
          }
        } catch (_) {}
        await login(response.data.token);
        navigate("/");
      } else {
        setErrorMessage(response.data.message || "Giriş başarısız oldu.");
      }
    } catch (err) {
      if (err.response) {
        const { status, data } = err.response;

        // Deneme bilgilerini güncelle
        if (data.attempts !== undefined) {
          setAttemptInfo({
            attempts: data.attempts,
            remainingAttempts: data.remainingAttempts,
            nextBlockAt: data.nextBlockAt
          });
        }

        switch (status) {
          case 401:
            setErrorMessage("Şifre hatalı!");
            break;
          case 404:
            setErrorMessage("Kullanıcı bulunamadı!");
            break;
          case 429:
            setIsBlocked(true);
            setBlockTimer(data.retryAfter || 60);
            setErrorMessage(data.message);
            if (data.attempts) {
              setAttemptInfo({ attempts: data.attempts, remainingAttempts: 0, nextBlockAt: data.attempts });
            }
            break;
          case 403:
            setErrorMessage(
              data?.message?.includes("CORS") || data?.error === "Origin not allowed"
                ? "Erişim reddedildi (CORS). Sunucuyu yeniden başlatın; backend ALLOW_LAN_ORIGINS=true olmalı."
                : data?.message || "Erişim reddedildi (403). Backend çalışıyor mu? (port 5000/5001)",
            );
            break;
          case 500:
            setErrorMessage("Sunucu hatası.");
            break;
          default:
            setErrorMessage("Bir hata oluştu.");
        }
      } else if (err.request) {
        const isTimeout = err.code === "ECONNABORTED" || err.message?.includes("timeout");
        setErrorMessage(isTimeout
          ? "Sunucuya ulaşılamadı (zaman aşımı). Wi‑Fi ve sunucu adresini kontrol edin."
          : "Sunucuya bağlanılamadı. Aynı Wi‑Fi ağını ve sunucu adresini kontrol edin.");
      } else {
        setErrorMessage(err?.message || "Bir hata oluştu.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const nextBlock = attemptInfo ? getNextBlockDuration(attemptInfo.attempts) : null;

  return (
    <div className={`login-page ${isMobile ? "login-page--mobile" : ""}`}>
      <div className="left-panel login-left-panel">
        <img src={loginReklam} alt="Ofis Uygulamaları" className="devices-image" />
        <a href="https://pozitifofis.com.tr" className="website-link">pozitifofis.com.tr</a>
      </div>

      <div className="right-panel login-right-panel">
        <form className="login-form" onSubmit={handleSubmit}>
          <h2>Giriş Yap</h2>

          {/* Engel Ekranı - Sadeleştirilmiş */}
          {isBlocked && (
              <div className="error-message">
                Çok fazla başarısız deneme yaptınız. {formatTime(blockTimer)}
              </div>

          )}

          {/* Deneme Durumu */}
          {attemptInfo && !isBlocked && attemptInfo.attempts > 0 && (
            <div className="error-message">
              Kullanıcı Adı veya Parola Hatalı. Kalan: {attemptInfo.remainingAttempts}
            </div>
          )}

          <div className="form-group">
            <input
              type="text"
              name="bayiKodu"
              placeholder="Bayi Kodu"
              value={bayiKodu}
              onChange={(e) => setBayiKodu(e.target.value)}
              disabled={isLoading || isBlocked}
              required
              autoComplete="organization"
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="username"
              placeholder="Kullanıcı Adı"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading || isBlocked}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              name="password"
              placeholder="Şifre"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || isBlocked}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="form-group checkbox-group login-remember-row" role="group" aria-label="Beni hatırla">
            <label className="remember-label">
              <input
                type="checkbox"
                checked={beniHatirla}
                onChange={(e) => setBeniHatirla(e.target.checked)}
                disabled={isLoading || isBlocked}
                aria-label="Beni hatırla"
              />
              <span className="remember-text">Beni hatırla</span>
            </label>
          </div>
          {/* Hata mesajı butonun hemen üstünde, mobilde görünsün */}
          {errorMessage && !isBlocked && (
            <div className="error-message error-message-prominent" role="alert">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? "Giriş Yapılıyor..." : "Giriş"}
            {!isLoading && <i className="icon-arrow-right"></i>}
          </button>
        </form>
        <footer>
          <p>&copy; Copyright 2025 Simzet Yazılım.</p>
        </footer>
      </div>
    </div>
  );
};

export default Login;